export interface SpeechActivityFrameAnalysis {
  rms: number;
  noiseFloor: number;
  dynamicRmsThreshold: number;
  voiceBandRatio: number;
  midBandRatio: number;
  lowBandRatio: number;
  highBandRatio: number;
  spectralCentroidHz: number;
  zeroCrossingRate: number;
  speechProbability: number;
}

export interface SpeechActivityDecisionOptions {
  startProbability: number;
  strongStartProbability: number;
  continueProbability: number;
  minimumStartMs: number;
  holdMs: number;
}

export interface SpeechActivityDecisionState {
  isSpeaking: boolean;
  candidateStartedAt: number | null;
  lastSpeechAt: number;
}

export const DEFAULT_SPEECH_ACTIVITY_DECISION_OPTIONS: SpeechActivityDecisionOptions = {
  startProbability: 0.68,
  strongStartProbability: 0.86,
  continueProbability: 0.48,
  minimumStartMs: 160,
  holdMs: 900,
};

export function createSpeechActivityDecisionState(): SpeechActivityDecisionState {
  return {
    isSpeaking: false,
    candidateStartedAt: null,
    lastSpeechAt: 0,
  };
}

export function analyzeMicrophoneSpeechFrame({
  timeSamples,
  frequencySamples,
  sampleRate,
  noiseFloor,
}: {
  timeSamples: Uint8Array | readonly number[];
  frequencySamples: Uint8Array | readonly number[];
  sampleRate: number;
  noiseFloor: number;
}): SpeechActivityFrameAnalysis {
  const rms = getAudioSampleRms(timeSamples);
  const spectrum = getSpeechSpectrumFeatures(frequencySamples, sampleRate);
  const zeroCrossingRate = getZeroCrossingRate(timeSamples);
  const dynamicRmsThreshold = Math.max(0.012, noiseFloor * 1.8);
  const rmsScore = smoothStep(dynamicRmsThreshold, dynamicRmsThreshold * 2.8, rms);
  const voiceBandScore = smoothStep(0.48, 0.72, spectrum.voiceBandRatio);
  const midBandScore = smoothStep(0.32, 0.55, spectrum.midBandRatio);
  const centroidScore = scoreWithinBand(spectrum.spectralCentroidHz, 140, 320, 3600, 5600);
  const zeroCrossingScore = scoreWithinBand(zeroCrossingRate, 0.012, 0.024, 0.18, 0.32);
  const highNoisePenalty = smoothStep(0.36, 0.62, spectrum.highBandRatio);
  const lowRumblePenalty = smoothStep(0.24, 0.5, spectrum.lowBandRatio);

  let speechProbability = clamp01(
    rmsScore * 0.28 +
    voiceBandScore * 0.28 +
    midBandScore * 0.18 +
    centroidScore * 0.16 +
    zeroCrossingScore * 0.1 -
    highNoisePenalty * 0.25 -
    lowRumblePenalty * 0.2
  );

  if (rms < dynamicRmsThreshold * 0.75) {
    speechProbability = Math.min(speechProbability, 0.2);
  }

  return {
    rms,
    noiseFloor,
    dynamicRmsThreshold,
    ...spectrum,
    zeroCrossingRate,
    speechProbability,
  };
}

export function updateSpeechActivityDecisionState({
  previousState,
  analysis,
  now,
  options = DEFAULT_SPEECH_ACTIVITY_DECISION_OPTIONS,
}: {
  previousState: SpeechActivityDecisionState;
  analysis: Pick<SpeechActivityFrameAnalysis, 'speechProbability'>;
  now: number;
  options?: SpeechActivityDecisionOptions;
}): SpeechActivityDecisionState {
  const probability = analysis.speechProbability;

  if (probability >= options.startProbability) {
    const candidateStartedAt = previousState.candidateStartedAt ?? now;
    const hasEnoughSpeechDuration = now - candidateStartedAt >= options.minimumStartMs;
    const hasStrongSpeechProbability = probability >= options.strongStartProbability;
    const isSpeaking = previousState.isSpeaking || hasEnoughSpeechDuration || hasStrongSpeechProbability;

    return {
      isSpeaking,
      candidateStartedAt: isSpeaking ? null : candidateStartedAt,
      lastSpeechAt: isSpeaking ? now : previousState.lastSpeechAt,
    };
  }

  if (previousState.isSpeaking && probability >= options.continueProbability) {
    return {
      isSpeaking: true,
      candidateStartedAt: null,
      lastSpeechAt: now,
    };
  }

  if (previousState.isSpeaking && now - previousState.lastSpeechAt < options.holdMs) {
    return {
      ...previousState,
      candidateStartedAt: null,
    };
  }

  return {
    isSpeaking: false,
    candidateStartedAt: null,
    lastSpeechAt: previousState.lastSpeechAt,
  };
}

function getAudioSampleRms(samples: Uint8Array | readonly number[]) {
  if (samples.length === 0) return 0;

  let sumSquares = 0;
  samples.forEach((sample) => {
    const centeredSample = (sample - 128) / 128;
    sumSquares += centeredSample * centeredSample;
  });

  return Math.sqrt(sumSquares / samples.length);
}

function getSpeechSpectrumFeatures(frequencySamples: Uint8Array | readonly number[], sampleRate: number) {
  const nyquistHz = sampleRate / 2;
  const binCount = frequencySamples.length;
  const binWidthHz = binCount > 0 ? nyquistHz / binCount : 0;
  let totalEnergy = 0;
  let voiceBandEnergy = 0;
  let midBandEnergy = 0;
  let lowBandEnergy = 0;
  let highBandEnergy = 0;
  let weightedFrequencyEnergy = 0;

  frequencySamples.forEach((sample, index) => {
    const frequencyHz = (index + 0.5) * binWidthHz;
    if (frequencyHz < 70 || frequencyHz > 8000) return;

    const energy = sample * sample;
    totalEnergy += energy;
    weightedFrequencyEnergy += energy * frequencyHz;

    if (frequencyHz < 120) {
      lowBandEnergy += energy;
    }
    if (frequencyHz >= 120 && frequencyHz <= 3400) {
      voiceBandEnergy += energy;
    }
    if (frequencyHz >= 300 && frequencyHz <= 3000) {
      midBandEnergy += energy;
    }
    if (frequencyHz >= 4200) {
      highBandEnergy += energy;
    }
  });

  return {
    voiceBandRatio: getRatio(voiceBandEnergy, totalEnergy),
    midBandRatio: getRatio(midBandEnergy, totalEnergy),
    lowBandRatio: getRatio(lowBandEnergy, totalEnergy),
    highBandRatio: getRatio(highBandEnergy, totalEnergy),
    spectralCentroidHz: totalEnergy > 0 ? weightedFrequencyEnergy / totalEnergy : 0,
  };
}

function getZeroCrossingRate(samples: Uint8Array | readonly number[]) {
  if (samples.length < 2) return 0;

  let crossings = 0;
  let previousSign = Math.sign(samples[0] - 128);

  for (let index = 1; index < samples.length; index += 1) {
    const centeredSample = samples[index] - 128;
    if (Math.abs(centeredSample) < 2) continue;

    const sign = Math.sign(centeredSample);
    if (previousSign !== 0 && sign !== 0 && sign !== previousSign) {
      crossings += 1;
    }
    if (sign !== 0) {
      previousSign = sign;
    }
  }

  return crossings / (samples.length - 1);
}

function getRatio(numerator: number, denominator: number) {
  if (denominator <= 0) return 0;
  return numerator / denominator;
}

function scoreWithinBand(value: number, lowStart: number, lowFull: number, highFull: number, highEnd: number) {
  if (value <= lowStart || value >= highEnd) return 0;
  if (value >= lowFull && value <= highFull) return 1;
  if (value < lowFull) return smoothStep(lowStart, lowFull, value);
  return 1 - smoothStep(highFull, highEnd, value);
}

function smoothStep(edge0: number, edge1: number, value: number) {
  if (edge0 === edge1) return value >= edge1 ? 1 : 0;
  const x = clamp01((value - edge0) / (edge1 - edge0));
  return x * x * (3 - 2 * x);
}

function clamp01(value: number) {
  if (!Number.isFinite(value)) return 0;
  return Math.min(1, Math.max(0, value));
}
